import apiClient from './client';

export interface OrderSummary {
  id: string;
  codice_ordine: string;
  customer_ragione_sociale: string;
  cer_code: string | null;
  // V48: riga CER d'ingresso dell'ordine — matching aree per id, non per codice.
  cer_attivo_id: string | null;
  status: string;
  // Sito di produzione (origine materiale) — sola lettura.
  origine_indirizzo: string | null;
  origine_comune: string | null;
  origine_provincia: string | null;
  origine_cap: string | null;
}

export interface AreaSummary {
  id: string;
  codice: string;
  descrizione: string;
  categoria: string;
  capacita_kg: number | null;  // NULL = capacità non configurata (#93)
}

export interface ConferimentoListItem {
  id: string;
  numero: string | null;
  order_id: string;
  codice_ordine: string;
  customer_ragione_sociale: string;
  data_prevista: string | null;
  peso_stimato_kg: number | null;
  peso_lordo_kg: number | null;
  peso_netto_kg: number | null;
  area_tentativa_codice: string | null;
  status: string;
  status_display: string;
  created_at: string;
  // Stato fatturazione (M10, #97).
  fatturato?: boolean;
  fattura_id?: string | null;
  numero_fattura?: string | null;
  stato_fattura?: string | null;
}

// R2-B: riferimento minimale all'omologa dell'ordine (fonte unica omologhe, #105).
export interface ConferimentoOmologaRef {
  id: string;
  stato_firma: 'bozza' | 'generata' | 'inviata' | 'firmata';
  pdf_firmato_url: string | null;
}

export interface ConferimentoDetail {
  id: string;
  numero: string | null;
  order_id: string;
  order: OrderSummary | null;
  omologa: ConferimentoOmologaRef | null;
  status: string;
  status_display: string;
  data_prevista: string | null;
  peso_stimato_kg: number | null;
  area_tentativa_id: string | null;
  area_tentativa: AreaSummary | null;
  note_programmazione: string | null;
  numero_fir: string | null;
  area_effettiva_id: string | null;
  area_effettiva: AreaSummary | null;
  peso_lordo_kg: number | null;
  controllo_radiometrico_ok: boolean | null;
  peso_netto_kg: number | null;
  // Nuovo modello #91 (Blocco 7): pesata/transizioni dedicate.
  tara_kg: number | null;
  peso_netto_origine: string | null;
  in_transito_at: string | null;
  in_transito_da: string | null;
  attesa_compliance_at: string | null;
  attesa_compliance_da: string | null;
  compliance_confermata_at: string | null;
  compliance_confermata_da: string | null;
  causale_respingimento: string | null;
  note: string | null;
  created_at: string;
  updated_at: string | null;
  // Stato fatturazione: fattura (bozza o emessa) collegata, se presente.
  fatturato?: boolean;
  fattura_id?: string | null;
  /** Passiva collegata: blocca l'eliminazione (409), non la modifica. */
  fattura_passiva_id?: string | null;
  numero_fattura?: string | null;
  stato_fattura?: string | null;
}

export interface ConferimentoListResponse {
  items: ConferimentoListItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface CreateConferimentoRequest {
  order_id: string;
  data_prevista: string;
  peso_stimato_kg: number;
  area_tentativa_id?: string;
  note_programmazione?: string;
}

export interface UpdateConferimentoRequest {
  data_prevista?: string;
  peso_stimato_kg?: number;
  area_tentativa_id?: string;
  numero_fir?: string;          // testo libero
  note_programmazione?: string;
  note?: string;
  // Correzione pesi reali (#2 Opzione B): netto/origine ricalcolati lato server.
  peso_lordo_kg?: number;
  tara_kg?: number;
  peso_netto_kg?: number;
  // Correzione date reali (R5/F4): ISO 8601 UTC. Solo fasi già avvenute;
  // guardie ordine/futuro lato server.
  in_transito_at?: string;
  attesa_compliance_at?: string;
  compliance_confermata_at?: string;
}

// Nuove transizioni #91 (Blocco 7 STEP 2).
export interface InTransitoRequest {
  numero_fir: string;
  controllo_radiometrico_ok?: boolean;
  peso_lordo_kg?: number;
}

export interface AttesaComplianceRequest {
  tara_kg?: number;
  peso_netto_kg?: number;  // #94: se presente, netto manuale; altrimenti calcolato lordo−tara
}

export interface InStoccaggioRequest {
  area_effettiva_id: string;
  override_capacita?: boolean;
}

export interface RiallocaConferimentoRequest {
  area_effettiva_id: string;
  override_capacita?: boolean;
}

export type RespingiCausale =
  | 'peso_eccedente' | 'non_conforme_omologa' | 'radiometrico'
  | 'documenti_mancanti' | 'altro';

export interface RespingiRequest {
  causale: RespingiCausale;
  motivo?: string;
}

export interface MaterialeInput {
  cer_uscita: string;
  materiale: string;
  peso_kg: number;
}

export async function listConferimenti(params: {
  order_id?: string;
  status?: string;
  data_da?: string;
  data_a?: string;
  area_id?: string;
  limit?: number;
  offset?: number;
} = {}): Promise<ConferimentoListResponse> {
  const res = await apiClient.get('/api/conferimenti', { params });
  return res.data;
}

export async function getConferimento(id: string): Promise<ConferimentoDetail> {
  const res = await apiClient.get(`/api/conferimenti/${id}`);
  return res.data;
}

export async function createConferimento(body: CreateConferimentoRequest): Promise<ConferimentoDetail> {
  const res = await apiClient.post('/api/conferimenti', body);
  return res.data;
}

export async function updateConferimento(id: string, body: UpdateConferimentoRequest): Promise<ConferimentoDetail> {
  const res = await apiClient.patch(`/api/conferimenti/${id}`, body);
  return res.data;
}

export async function deleteConferimento(id: string): Promise<void> {
  await apiClient.delete(`/api/conferimenti/${id}`);
}

// Gruppo F: riallocazione area di un conferimento già stoccato (movimento ledger BE).
/**
 * Elimina un conferimento GIÀ STOCCATO: soft-delete + storno a ledger.
 *
 * DIVERSO da `deleteConferimento`, che vale solo per un `programmato` — quello
 * a ledger non è mai entrato e non ha niente da stornare. Solo admin.
 * Il `motivo` è obbligatorio: a ledger lo storno azzera il netto e il controllo
 * 2 della diagnostica smette di vedere la riga, quindi resta l'unica traccia.
 */
export async function eliminaConferimentoStoccato(
  id: string, motivo: string,
): Promise<{ id: string; movimenti_compensazione: number }> {
  const { data } = await apiClient.post<{ id: string; movimenti_compensazione: number }>(
    `/api/conferimenti/${id}/elimina-stoccato`, { motivo });
  return data;
}

export async function riallocaConferimento(id: string, body: RiallocaConferimentoRequest): Promise<ConferimentoDetail> {
  const res = await apiClient.post(`/api/conferimenti/${id}/rialloca`, body);
  return res.data;
}

export async function inTransito(id: string, body: InTransitoRequest): Promise<ConferimentoDetail> {
  const res = await apiClient.post(`/api/conferimenti/${id}/in-transito`, body);
  return res.data;
}

export async function attesaCompliance(id: string, body: AttesaComplianceRequest): Promise<ConferimentoDetail> {
  const res = await apiClient.post(`/api/conferimenti/${id}/attesa-compliance`, body);
  return res.data;
}

export async function inStoccaggio(id: string, body: InStoccaggioRequest): Promise<ConferimentoDetail> {
  const res = await apiClient.post(`/api/conferimenti/${id}/in-stoccaggio`, body);
  return res.data;
}

export async function respingiConferimento(id: string, body: RespingiRequest): Promise<ConferimentoDetail> {
  const res = await apiClient.post(`/api/conferimenti/${id}/respingi`, body);
  return res.data;
}
